/**
 * AnalyticsDashboard — V35.10
 *
 * Tableau de bord Analytics ultra-professionnel pour le Super Admin :
 *   - Header premium gradient + sélecteur période
 *   - Live ticker visiteurs (5min) avec halo animé
 *   - 4 KPI cards en grille avec micro-trends
 *   - Bloc Géographie : carte Leaflet (top) + Top pays + Top villes (côte à côte)
 *   - Bloc Engagement : devices donut + pages populaires + navigateurs
 *   - Bloc Tendance temporelle (Recharts AreaChart)
 *   - États empty + loading premium
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import RefreshButton from './ui/RefreshButton';
import { Card, CardContent, CardHeader } from './ui/card';

import { Badge } from './ui/badge';
import { Users, Eye, Monitor, Smartphone, Tablet, MapPin, TrendingUp, Globe, Activity, MousePointer, Building2, Loader2, Sparkles, Earth, Compass, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { AreaChart, Area, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import axios from 'axios';
import GeoVisitorsMap from './Analytics/GeoVisitorsMap';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const DEVICE_META = {
  desktop: { label: 'Ordinateur', icon: Monitor, color: '#2563eb', soft: 'bg-blue-50 text-blue-700' },
  mobile:  { label: 'Mobile',     icon: Smartphone, color: '#10b981', soft: 'bg-emerald-50 text-emerald-700' },
  tablet:  { label: 'Tablette',   icon: Tablet, color: '#f59e0b', soft: 'bg-amber-50 text-amber-700' },
};

// ------------------------------------------------------------------
// Sous-composants
// ------------------------------------------------------------------
function HBar({ label, value, total, color = '#0B3C5D', emoji = null, sublabel = null }) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-2 min-w-0">
          {emoji && <span className="text-base leading-none flex-shrink-0">{emoji}</span>}
          <span className="font-medium text-slate-700 dark:text-slate-200 truncate" title={label}>
            {label}
          </span>
          {sublabel && (
            <span className="text-slate-400 dark:text-slate-500 truncate hidden sm:inline">
              · {sublabel}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="font-mono font-semibold text-slate-900 dark:text-white">{value}</span>
          <span className="text-slate-400 dark:text-slate-500 w-12 text-right">
            {pct.toFixed(1)}%
          </span>
        </div>
      </div>
      <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${Math.max(pct, 1.5)}%`, background: color }}
        />
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, accent, trend, suffix }) {
  return (
    <Card className="relative overflow-hidden border-slate-200/80 dark:border-slate-700 hover:shadow-md transition-shadow">
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage: `radial-gradient(${accent} 1px, transparent 1px)`,
          backgroundSize: '14px 14px',
        }}
      />
      <CardContent className="pt-5 pb-5 relative">
        <div className="flex items-start justify-between">
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center shadow-sm"
            style={{ background: `${accent}1a`, color: accent }}
          >
            <Icon className="w-5 h-5" />
          </div>
          {trend && (
            <Badge
              className="text-[10px] font-bold px-2 py-0.5"
              style={{ background: `${accent}1a`, color: accent, border: `1px solid ${accent}33` }}
            >
              {trend}
            </Badge>
          )}
        </div>
        <p className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 mt-4 font-semibold">
          {label}
        </p>
        <p className="text-3xl font-black text-slate-900 dark:text-white mt-1 leading-none">
          {value}
          {suffix && <span className="text-base font-medium text-slate-400 ml-1">{suffix}</span>}
        </p>
      </CardContent>
    </Card>
  );
}

function SectionHeader({ icon: Icon, title, subtitle, accent = '#0B3C5D', right = null }) {
  return (
    <div className="flex items-end justify-between gap-3 mb-3">
      <div className="flex items-center gap-3">
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0"
          style={{ background: `${accent}14`, color: accent }}
        >
          <Icon className="w-4.5 h-4.5" />
        </div>
        <div>
          <h3 className="text-base font-bold text-slate-900 dark:text-white leading-tight">{title}</h3>
          {subtitle && (
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{subtitle}</p>
          )}
        </div>
      </div>
      {right}
    </div>
  );
}

// Donut SVG custom (pas de dep) pour les devices
function DeviceDonut({ devices = {}, percentages = {} }) {
  const entries = Object.entries(devices);
  const total = entries.reduce((s, [, v]) => s + v, 0);
  if (total === 0) {
    return <div className="text-center text-sm text-slate-400 py-8">Aucune donnée</div>;
  }
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;
  return (
    <div className="flex items-center gap-5">
      <div className="relative flex-shrink-0">
        <svg width="140" height="140" viewBox="0 0 140 140" className="-rotate-90">
          <circle cx="70" cy="70" r={radius} fill="none" stroke="#e2e8f0" strokeWidth="14" />
          {entries.map(([device, count]) => {
            const meta = DEVICE_META[device] || DEVICE_META.desktop;
            const ratio = count / total;
            const dash = ratio * circumference;
            const circle = (
              <circle
                key={device}
                cx="70" cy="70" r={radius}
                fill="none" stroke={meta.color} strokeWidth="14"
                strokeDasharray={`${dash} ${circumference - dash}`}
                strokeDashoffset={-offset}
                strokeLinecap="butt"
              />
            );
            offset += dash;
            return circle;
          })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-black text-slate-900 dark:text-white">{total}</span>
          <span className="text-[10px] uppercase tracking-wider text-slate-500">visites</span>
        </div>
      </div>
      <div className="flex-1 space-y-2.5">
        {entries.map(([device, count]) => {
          const meta = DEVICE_META[device] || DEVICE_META.desktop;
          const Icon = meta.icon;
          const pct = percentages[device] ?? ((count / total) * 100).toFixed(1);
          return (
            <div key={device} className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <span
                  className="w-7 h-7 rounded-lg flex items-center justify-center"
                  style={{ background: `${meta.color}1a`, color: meta.color }}
                >
                  <Icon className="w-3.5 h-3.5" />
                </span>
                <span className="font-medium text-slate-700 dark:text-slate-200">{meta.label}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-mono font-bold text-slate-900 dark:text-white">{count}</span>
                <span className="text-xs text-slate-400 w-10 text-right">{pct}%</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// Composant principal
// ------------------------------------------------------------------
export default function AnalyticsDashboard() {
  const { getAuthHeader } = useAuth();

  const [analytics, setAnalytics] = useState(null);
  const [realtime, setRealtime] = useState(null);
  const [geo, setGeo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);

  const fetchAll = useCallback(async ({ silent = false } = {}) => {
    setLoading(true);
    try {
      const [summaryRes, geoRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/analytics/summary?days=${period}`, { headers: getAuthHeader() }),
        axios.get(`${BACKEND_URL}/api/admin/analytics/geo?days=${period}`, { headers: getAuthHeader() }),
      ]);
      setAnalytics(summaryRes.data.analytics);
      setGeo(geoRes.data.geo);
      if (!silent) {
        toast.success('Statistiques actualisées');
      }
    } catch {
      toast.error("Erreur lors du chargement des statistiques");
    } finally {
      setLoading(false);
    }
  }, [period, getAuthHeader]);

  const fetchRealtime = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/analytics/realtime?minutes=5`, { headers: getAuthHeader() });
      setRealtime(res.data.realtime);
    } catch {
      // silent
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchAll({ silent: true });
    fetchRealtime();
    const id = setInterval(fetchRealtime, 30000);
    return () => clearInterval(id);
  }, [fetchAll, fetchRealtime]);

  if (loading && !analytics) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[420px] gap-3" data-testid="analytics-loading">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
        <p className="text-sm text-slate-500">Chargement des analytics…</p>
      </div>
    );
  }

  const totalVisits = analytics?.total_visits || 0;
  const visitsByDay = analytics?.visits_by_day || [];
  const topPages = (analytics?.top_pages || []).slice(0, 6);
  const topBrowsers = (analytics?.top_browsers || []).slice(0, 5);
  const topCountries = geo?.top_countries || [];
  const topCities = (geo?.top_cities || []).slice(0, 8);
  const mapPoints = geo?.map_points || [];

  const maxPage = Math.max(...topPages.map((p) => p.visits), 1);
  const maxBrowser = Math.max(...topBrowsers.map((b) => b.visits), 1);

  return (
    <div className="space-y-7" data-testid="analytics-dashboard-v35-10">
      {/* === HEADER PREMIUM === */}
      <div
        className="relative overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm"
        style={{
          background:
            'linear-gradient(135deg, #0B3C5D 0%, #0a1e3f 60%, #091a36 100%)',
        }}
      >
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none">
          <div className="absolute top-0 right-0 w-80 h-80 bg-white rounded-full blur-3xl -translate-y-32 translate-x-24" />
          <div className="absolute bottom-0 left-1/3 w-64 h-64 bg-amber-300 rounded-full blur-3xl translate-y-32" />
        </div>
        <div className="relative px-6 sm:px-8 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-white">
          <div className="flex items-start gap-4">
            <div className="hidden sm:flex w-14 h-14 rounded-2xl bg-white/10 border border-white/20 backdrop-blur-md items-center justify-center shadow-xl flex-shrink-0">
              <Sparkles className="w-6 h-6 text-amber-300" />
            </div>
            <div>
              <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.22em] text-white/70 mb-1.5">
                <span className="inline-block w-6 h-px bg-amber-300/70" />
                <span className="font-semibold">O&apos;SCOP</span>
                <span className="text-white/40">|</span>
                <span>Analytics</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-black tracking-tight leading-tight">
                Statistiques de fréquentation
              </h2>
              <p className="text-sm text-white/75 mt-1 max-w-2xl">
                Tableau de bord temps réel · localisation visiteurs · engagement & funnels de la plateforme.
                <span className="ml-2 inline-flex items-center gap-1 text-amber-200/90 text-xs">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-300 animate-pulse" />
                  Rétention 30 jours · RGPD compliant
                </span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <select
              value={period}
              onChange={(e) => setPeriod(Number(e.target.value))}
              className="bg-white/10 border border-white/20 backdrop-blur-md text-white text-sm rounded-lg px-3 py-2 font-medium focus:outline-none focus:ring-2 focus:ring-amber-300/50"
              data-testid="analytics-period-select"
            >
              <option value={7} className="text-slate-900">7 derniers jours</option>
              <option value={30} className="text-slate-900">30 derniers jours</option>
              <option value={90} className="text-slate-900">90 derniers jours</option>
            </select>
            <RefreshButton
              onRefresh={async () => { await fetchAll(); }}
              loading={loading}
              successMessage="Statistiques actualisées"
              variant="light"
              testId="analytics-refresh-btn"
            />
          </div>
        </div>
      </div>

      {/* === LIVE TICKER === */}
      {realtime && (
        <div
          className="rounded-2xl border border-emerald-200 dark:border-emerald-800/60 bg-gradient-to-r from-emerald-50 via-emerald-50/70 to-white dark:from-emerald-900/30 dark:via-emerald-900/10 dark:to-transparent px-5 py-4 flex items-center gap-4"
          data-testid="analytics-realtime"
        >
          <div className="relative flex-shrink-0">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/15 flex items-center justify-center">
              <Activity className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
            </div>
            <span className="absolute -top-0.5 -right-0.5 flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" />
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-2xl font-black text-emerald-800 dark:text-emerald-200 leading-none">
              {realtime.active_visitors}
              <span className="text-sm font-semibold text-emerald-700/80 ml-2">visiteurs en ligne</span>
            </p>
            <p className="text-xs text-emerald-700/80 dark:text-emerald-300/70 mt-1">
              Mesure sur les 5 dernières minutes · rafraîchissement toutes les 30s
            </p>
          </div>
          {realtime.active_pages?.[0] && (
            <div className="text-right hidden md:block">
              <p className="text-[10px] uppercase tracking-wider text-emerald-600/80 font-bold">Page la + active</p>
              <p className="font-mono text-sm font-semibold text-emerald-900 dark:text-emerald-100 truncate max-w-[200px]">
                {realtime.active_pages[0].page}
              </p>
            </div>
          )}
        </div>
      )}

      {/* === KPI CARDS === */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={Eye} label="Visites totales" value={totalVisits} accent="#2563eb" />
        <KpiCard icon={Users} label="Visiteurs uniques" value={analytics?.unique_visitors || 0} accent="#10b981" />
        <KpiCard icon={Globe} label="Pays touchés" value={geo?.unique_countries || 0} accent="#f59e0b" />
        <KpiCard icon={MapPin} label="Villes" value={geo?.unique_cities || 0} accent="#8b5cf6" />
      </div>

      {/* === BLOC GÉOGRAPHIE === */}
      <Card className="border-slate-200/80 dark:border-slate-700 overflow-hidden">
        <CardHeader className="border-b border-slate-100 dark:border-slate-800 bg-gradient-to-br from-slate-50 to-white dark:from-slate-900 dark:to-slate-900/40 pb-4">
          <SectionHeader
            icon={Earth}
            title="Localisation des visiteurs"
            subtitle="Carte mondiale interactive · base GeoLite2 locale · respect RGPD"
            accent="#0B3C5D"
            right={
              <div className="hidden sm:flex items-center gap-1.5 text-xs text-slate-500">
                <Compass className="w-3.5 h-3.5" />
                <span>Survolez les cercles pour les détails</span>
              </div>
            }
          />
        </CardHeader>
        <CardContent className="pt-5 space-y-5">
          <GeoVisitorsMap points={mapPoints} height={380} />

          <div className="grid md:grid-cols-2 gap-5">
            {/* TOP PAYS */}
            <div data-testid="top-countries-block">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  <Globe className="w-4 h-4 text-blue-600" />
                  Top pays
                </h4>
                <Badge className="bg-blue-600 text-white/10 text-blue-700 border-blue-200 dark:bg-blue-500/20 dark:text-blue-300 font-semibold">
                  {topCountries.length} pays
                </Badge>
              </div>
              <div className="space-y-3 max-h-[340px] overflow-y-auto pr-1">
                {topCountries.length === 0 ? (
                  <p className="text-sm text-slate-400 py-6 text-center">Aucune donnée.</p>
                ) : (
                  topCountries.map((c) => (
                    <HBar
                      key={c.country_code + c.country}
                      label={c.country}
                      sublabel={c.country_code}
                      value={c.visits}
                      total={totalVisits}
                      color="#0B3C5D"
                      emoji={c.flag}
                    />
                  ))
                )}
              </div>
            </div>

            {/* TOP VILLES */}
            <div data-testid="top-cities-block">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-amber-600" />
                  Top villes
                </h4>
                <Badge className="bg-amber-600 text-white/10 text-amber-700 border-amber-800 dark:bg-amber-500/20 dark:text-amber-300 font-semibold">
                  {topCities.length} villes
                </Badge>
              </div>
              <div className="space-y-3 max-h-[340px] overflow-y-auto pr-1">
                {topCities.length === 0 ? (
                  <p className="text-sm text-slate-400 py-6 text-center">
                    Pas encore de données ville.
                  </p>
                ) : (
                  topCities.map((c, i) => (
                    <HBar
                      key={`${c.country_code}-${c.city}-${i}`}
                      label={c.city}
                      sublabel={c.country}
                      value={c.visits}
                      total={totalVisits}
                      color="#f59e0b"
                      emoji={c.flag}
                    />
                  ))
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* === BLOC ENGAGEMENT === */}
      <div className="grid lg:grid-cols-3 gap-5">
        <Card className="border-slate-200/80 dark:border-slate-700">
          <CardHeader className="pb-2">
            <SectionHeader
              icon={Monitor}
              title="Appareils"
              subtitle="Répartition des devices"
              accent="#2563eb"
            />
          </CardHeader>
          <CardContent>
            <DeviceDonut
              devices={analytics?.devices || {}}
              percentages={analytics?.devices_percentage || {}}
            />
          </CardContent>
        </Card>

        <Card className="border-slate-200/80 dark:border-slate-700">
          <CardHeader className="pb-2">
            <SectionHeader
              icon={MousePointer}
              title="Pages populaires"
              subtitle={`${topPages.length} pages mesurées`}
              accent="#8b5cf6"
            />
          </CardHeader>
          <CardContent className="space-y-2.5">
            {topPages.length === 0 ? (
              <p className="text-center text-sm text-slate-400 py-6">Aucune donnée</p>
            ) : (
              topPages.map((p, i) => (
                <div key={i} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-medium text-slate-700 dark:text-slate-200 truncate flex items-center gap-1.5">
                      <ChevronRight className="w-3 h-3 text-slate-400" />
                      {p.page || 'Accueil'}
                    </span>
                    <span className="font-mono font-bold text-slate-900 dark:text-white">{p.visits}</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-violet-500 to-purple-600 rounded-full transition-all duration-500"
                      style={{ width: `${(p.visits / maxPage) * 100}%` }}
                    />
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200/80 dark:border-slate-700">
          <CardHeader className="pb-2">
            <SectionHeader
              icon={Globe}
              title="Navigateurs"
              subtitle="Top 5"
              accent="#06b6d4"
            />
          </CardHeader>
          <CardContent className="space-y-2.5">
            {topBrowsers.length === 0 ? (
              <p className="text-center text-sm text-slate-400 py-6">Aucune donnée</p>
            ) : (
              topBrowsers.map((b, i) => (
                <div key={i} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-medium text-slate-700 dark:text-slate-200 truncate">{b.browser}</span>
                    <span className="font-mono font-bold text-slate-900 dark:text-white">{b.visits}</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-cyan-500 to-teal-600 rounded-full transition-all duration-500"
                      style={{ width: `${(b.visits / maxBrowser) * 100}%` }}
                    />
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      {/* === TENDANCE TEMPORELLE === */}
      {visitsByDay.length > 0 && (
        <Card className="border-slate-200/80 dark:border-slate-700">
          <CardHeader className="pb-2">
            <SectionHeader
              icon={TrendingUp}
              title="Tendance des visites"
              subtitle={`Évolution sur ${period} jours`}
              accent="#0ea5e9"
            />
          </CardHeader>
          <CardContent>
            <div style={{ width: '100%', height: 220 }}>
              <ResponsiveContainer>
                <AreaChart data={visitsByDay} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorVisits" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0ea5e9" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#0ea5e9" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    tickFormatter={(v) => (v || '').slice(5)}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <RechartsTooltip
                    contentStyle={{
                      background: 'white', border: '1px solid #e2e8f0',
                      borderRadius: 8, fontSize: 12, fontWeight: 600,
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="visits"
                    stroke="#0ea5e9"
                    strokeWidth={2.5}
                    fill="url(#colorVisits)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
